export interface FullscreenPort {
  supported(): boolean;
  active(): boolean;
  enter(): Promise<void>;
  exit(): Promise<void>;
  onChange(listener: () => void): void;
}

export interface CityViewState {
  expanded: boolean;
  fullscreen: boolean;
  busy: boolean;
  notice: string;
}

/** Presentation only: never starts, seeks or resets the simulation. */
export class CityViewController {
  private state: CityViewState = {
    expanded: false,
    fullscreen: false,
    busy: false,
    notice: "",
  };
  private listeners = new Set<(state: CityViewState) => void>();

  constructor(private readonly port: FullscreenPort) {
    port.onChange(() => {
      this.state.fullscreen = port.active();
      if (this.state.fullscreen) this.state.expanded = true;
      this.emit();
    });
  }

  snapshot(): CityViewState {
    return { ...this.state };
  }

  subscribe(listener: (state: CityViewState) => void): void {
    this.listeners.add(listener);
    listener(this.snapshot());
  }

  async toggleExpanded(): Promise<void> {
    if (this.state.busy) return;
    this.state.notice = "";
    if (this.state.expanded && this.port.active()) {
      if (!(await this.leaveFullscreen())) return;
    }
    this.state.expanded = !this.state.expanded;
    this.emit();
  }

  async toggleFullscreen(): Promise<void> {
    if (this.state.busy) return;
    this.state.notice = "";
    if (this.port.active()) {
      await this.leaveFullscreen();
      return;
    }
    this.state.expanded = true;
    if (!this.port.supported()) {
      this.state.notice =
        "Fullscreen is unavailable here. Cinema view still fills this browser window.";
      this.emit();
      return;
    }
    this.state.busy = true;
    this.emit();
    try {
      // Keep this request in the original button gesture; do not defer it.
      await this.port.enter();
    } catch {
      this.state.notice =
        "Fullscreen was not allowed. You can continue in cinema view.";
    } finally {
      this.state.fullscreen = this.port.active();
      this.state.busy = false;
      this.emit();
    }
  }

  private async leaveFullscreen(): Promise<boolean> {
    this.state.busy = true;
    this.emit();
    try {
      await this.port.exit();
    } catch {
      this.state.notice =
        "Use Esc or your browser's fullscreen control to leave fullscreen.";
    } finally {
      this.state.fullscreen = this.port.active();
      this.state.busy = false;
      this.emit();
    }
    return !this.state.fullscreen;
  }

  private emit(): void {
    for (const listener of this.listeners) listener(this.snapshot());
  }
}

export function browserFullscreenPort(element: HTMLElement): FullscreenPort {
  // Cinema view is the fallback for browsers without the standard Fullscreen API.
  const doc = element.ownerDocument;
  return {
    supported: () =>
      typeof element.requestFullscreen === "function" &&
      Boolean(doc.fullscreenEnabled),
    active: () => doc.fullscreenElement === element,
    enter: () => element.requestFullscreen(),
    exit: () => doc.exitFullscreen(),
    onChange: (listener) => {
      doc.addEventListener("fullscreenchange", listener);
    },
  };
}
